import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Col, Row, Form, Button, Skeleton, Modal, message } from "antd";
import { CheckOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import ClickableTextButton from "components/ClickableTextButton/ClickableTextButton";
import FormInput from "components/FormInput/FormInput";
import FormPasswordInput from "components/FormPasswordInput/FormPasswordInput";
import FormSelection from "components/FormSelection/FormSelection";
import FormGroupSelectionInput from "components/FormGroupSelectionInput/FormGroupSelectionInput";
import ScrollableContainer from "components/ScrollableContainer/ScrollableContainer";
import FormUpload from "components/Upload/FormUpload/FormUpload";
import SectionCard from "components/SectionCard/SectionCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import Title from "components/Title/Title";

import { useFetchConstant } from "apis/constant";
import {
  postCreateUser,
  useGetUserById,
  patchUpdateUser,
  deleteUser,
  refetchUser,
} from "apis/user";
import { buildUsersUri, buildUserUri } from "utils/routes";

import {
  DeleteCol,
  RPContainer,
  RPTitle,
  RPDesc,
  RPRequirement,
} from "./User.styles";

import Locale from "react-intl-universal";

const { useForm } = Form;
const { success, error, confirm } = Modal;

const User = () => {
  const history = useHistory();
  const [form] = useForm();
  const { id: userId } = useParams();

  const isEditMode = !!userId;

  const {
    isLoading: isCountryCodesLoading,
    selection: countryCodes,
  } = useFetchConstant("countries", "phoneCode", "phoneCode");

  const {
    isLoading: isAvailableCountryLoading,
    selection: availableCountry,
  } = useFetchConstant("countries", "name", "name");

  const { isLoading: isRolesLoading, selection: roles } = useFetchConstant(
    "roles"
  );
  const {
    isLoading: isAccountStatusesLoading,
    selection: accountStatuses,
  } = useFetchConstant("accountStatuses");
  const { isLoading: isUserLoading, data: user } = useGetUserById(
    userId,
    isEditMode
  );

  const isLoading =
    isAvailableCountryLoading ||
    isCountryCodesLoading ||
    isRolesLoading ||
    isAccountStatusesLoading ||
    isUserLoading;
  const pageTitle = isEditMode
    ? Locale.get("Users.editUser")
    : Locale.get("Users.add_NewUser.pageTitle");
  const rpTitle = isEditMode
    ? Locale.get("Users.resetPassword")
    : Locale.get("Users.add_NewUser.setPassword");
  const initialValues = isEditMode
    ? user
    : { contact: { countryCode: countryCodes[0] && countryCodes[0].value } };

  const handleOnSaveButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
      };

      if (isEditMode) {
        const warningMsg = payload.password
          ? {
              title: Locale.get("Users.resetPasswordWarning"),
              content: Locale.get("Users.resetPasswordContent"),
            }
          : {
              title: Locale.get("Users.editWarningMsg"),
              content: Locale.get("Users.editWarningContent"),
            };
        confirm({
          ...warningMsg,
          icon: <ExclamationCircleOutlined />,
          onOk() {
            patchUpdateUser(userId, payload)
              .then((updatedUser) => {
                message.success(Locale.get("Users.updateSuccessfully"));
                refetchUser(updatedUser._id);
              })
              .catch((ex) => {
                error({
                  title: ex.message,
                });
              });
          },
          onCancel() {},
        });
      } else {
        postCreateUser(payload)
          .then((createdUser) => {
            message.success(Locale.get("Users.createSuccess"));
            history.push(buildUserUri(createdUser._id));
          })
          .catch((ex) => {
            error({
              title: ex.message,
            });
          });
      }
    } catch (error) {
      error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const handleOnDeleteButtonClick = () => {
    confirm({
      title: Locale.get("Users.deleteTitle"),
      content: Locale.get("Users.deleteContent"),
      icon: <ExclamationCircleOutlined />,
      onOk() {
        deleteUser(userId)
          .then(() => {
            success({
              title: Locale.get("Users.deleteUserSuccess"),
              onOk() {
                history.push(buildUsersUri());
              },
            });
          })
          .catch((ex) => {
            error({
              title: ex.message,
            });
          });
      },
      onCancel() {},
    });
  };

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Form form={form} initialValues={initialValues}>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: "32px" }}
          >
            <ClickableTextButton
              text={Locale.get("Users.add_NewUser.backNav")}
              url={buildUsersUri()}
              showIcon={true}
              type="back"
            />
            <Row align="middle">
              {isEditMode && (
                <DeleteCol>
                  <ClickableTextButton
                    text={Locale.get("Users.delete")}
                    onClick={handleOnDeleteButtonClick}
                  />
                </DeleteCol>
              )}
              <Col span={12}>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleOnSaveButtonClick}
                >
                  {Locale.get("Users.add_NewUser.saveButton")}
                </Button>
              </Col>
            </Row>
          </Row>
          <ScrollableContainer>
            <Title marginBottom="32px">{pageTitle}</Title>
            <SectionContainer>
              <SectionCard
                title={Locale.get("Users.sectionCard.sectionCardTitle_0")}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Users.sectionCard.labelFName")}
                      name={["name", "firstName"]}
                      placeholder="John"
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_0a"
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Users.sectionCard.labelLName")}
                      name={["name", "lastName"]}
                      placeholder="Doe"
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_0b"
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelection
                      label={Locale.get("Users.sectionCard.labelCountry")}
                      name="country"
                      placeholder={Locale.get(
                        "Users.sectionCard.placeHolderCountry"
                      )}
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_0c"
                      )}
                      selections={availableCountry}
                    />
                  </Col>
                  <Col span={12}>
                    <FormGroupSelectionInput
                      shouldUpdateField={"contactNumberUpdate"}
                      label={Locale.get("Users.sectionCard.contactNo")}
                      inputName={["contact", "contactNumber"]}
                      selectionName={["contact", "countryCode"]}
                      inputPlaceholder={Locale.get(
                        "Users.sectionCard.placeHolderOptional"
                      )}
                      inputType="number"
                      selections={countryCodes}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Users.sectionCard.email")}
                      name="email"
                      placeholder="your_email@gmail.com"
                      type="email"
                    />
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
            <SectionContainer>
              <SectionCard
                title={Locale.get("Users.sectionCard.sectionCardTitle_1")}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Users.sectionCard.labelCompanyName")}
                      name={["company", "name"]}
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_1a"
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <FormUpload
                      label={Locale.get("Users.sectionCard.logo")}
                      name={["company", "logo"]}
                      acceptTypes={["image/*"]}
                      isDisplayCard
                      labelButtonUpload={Locale.get("Users.sectionCard.upload")}
                      fileSizeLimitInMb={1}
                      tooltipMessage={Locale.get("Users.sectionCard.ttM")}
                    />
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
            <SectionContainer>
              <SectionCard
                title={Locale.get("Users.sectionCard.sectionCardTitle_2")}
              >
                <Row gutter={8}>
                  <Col span={12}>
                    <FormInput
                      label={Locale.get("Users.sectionCard.labelUsername")}
                      name="username"
                      placeholder="username123"
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_2a"
                      )}
                      disabled={isEditMode}
                    />
                    <FormSelection
                      label={Locale.get("Users.sectionCard.labelRole")}
                      name="role"
                      placeholder={Locale.get(
                        "Users.sectionCard.placeHolderRole"
                      )}
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_2b"
                      )}
                      selections={roles}
                    />
                    <FormSelection
                      label={Locale.get("Users.sectionCard.labelStatus")}
                      name="status"
                      placeholder={Locale.get(
                        "Users.sectionCard.placeHolderStatus"
                      )}
                      requiredErrorMessage={Locale.get(
                        "Users.sectionCard.errMsg_2c"
                      )}
                      selections={accountStatuses}
                    />
                  </Col>
                  <Col span={12}>
                    <RPContainer>
                      <RPTitle>{rpTitle}</RPTitle>
                      <RPDesc>{Locale.get("Users.sectionCard.rpDesc")}</RPDesc>
                      <RPRequirement>
                        {Locale.get("Users.sectionCard.rpReq")}
                      </RPRequirement>
                      <FormPasswordInput
                        name="password"
                        placeholder="P@ssW0rd"
                        requiredErrorMessage={
                          !isEditMode &&
                          Locale.get("Users.sectionCard.errMsg_2d")
                        }
                        checkPattern={true}
                      />
                    </RPContainer>
                  </Col>
                </Row>
              </SectionCard>
            </SectionContainer>
          </ScrollableContainer>
        </Form>
      )}
    </>
  );
};

export default User;
